import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import { useCreateFieldGroup } from "../../../../hooks/useLibraries";
import { useFieldGroupsContext } from "../../../../contexts/fieldGroupsContext";

interface CreateLibraryTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (fieldGroupId: string) => void;
}

export function CreateLibraryTypeModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateLibraryTypeModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slugPrefix, setSlugPrefix] = useState("");

  const { createFieldGroup, loading } = useCreateFieldGroup();
  const { refetch: refetchFieldGroups } = useFieldGroupsContext();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setSlugPrefix("");
    }
  }, [open]);

  // Auto-generate slug prefix from name
  useEffect(() => {
    if (name && !slugPrefix) {
      // Take first letter of each word, uppercase
      const prefix = name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
      setSlugPrefix(prefix);
    }
  }, [name, slugPrefix]);

  const handleSubmit = async () => {
    if (!name.trim() || !slugPrefix.trim()) return;

    const result = await createFieldGroup({
      name: name.trim(),
      slugPrefix: slugPrefix.trim(), // Required
      description: description.trim() || null,
    });

    if (result) {
      await refetchFieldGroups();
      onSuccess(result.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Library</DialogTitle>
          <DialogDescription>
            Create a new library type to organize related items. Examples:
            Risks, Systems, Processes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="library-name">Library Name *</Label>
            <Input
              id="library-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Risks, Systems, Processes"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug-prefix">ID Prefix *</Label>
            <Input
              id="slug-prefix"
              value={slugPrefix}
              onChange={(e) => setSlugPrefix(e.target.value.toUpperCase())}
              placeholder="e.g., RPT, SYS, PROC"
              maxLength={10}
            />
            <p className="text-xs text-stone-500">
              Items will be numbered with this prefix (e.g.,{" "}
              {slugPrefix || "RPT"}
              -001, {slugPrefix || "RPT"}-002)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="library-description">Description</Label>
            <Textarea
              id="library-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a description for this library (optional)"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !slugPrefix.trim() || loading}
            loading={loading}
          >
            Create Library
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
